'use client';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/redux/store';
import { setActiveCategories } from '@/redux/reducers/categorySlice';
import type { Category } from '@/types/category';
import { getCategories } from '@/services/strapiService';
import CategoryLink from './CategoryLink';
import { useTranslate } from '@tolgee/react';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const CategoryNav: FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const searchParams = useSearchParams();
  const router = useRouter();
  const dispatch = useDispatch();
  const activeCategories = useSelector((state: RootState) => state.category.activeCategories);
  const locale = useSelectedLanguage();
  const { t } = useTranslate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories(locale);
        const categoriesWithAll = [
          {
            id: 0,
            attributes: {
              slug: 'all',
              Name: t('category_selection.all.tag.label'),
            },
          },
          ...response.data,
        ];
        setCategories(categoriesWithAll);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    void fetchCategories();
  }, [locale]);

  useEffect(() => {
    if (searchParams) {
      const category = searchParams.get('category');
      if (category) {
        const categories = category.split(',');
        dispatch(setActiveCategories(categories));
      }
    }
  }, [searchParams, dispatch]);

  const toggleCategoryHandler = (anchor: string) => {
    let newCategories: string[];

    if (anchor === 'all') {
      newCategories = ['all'];
      dispatch(setActiveCategories(['all']));
    } else {
      newCategories = activeCategories.includes(anchor)
        ? activeCategories.filter((c) => c !== anchor)
        : [...activeCategories.filter((c) => c !== 'all'), anchor];

      if (newCategories.length === 0) {
        newCategories = ['all'];
      }

      dispatch(setActiveCategories(newCategories));
    }

    void router.push(`?category=${newCategories?.join(',')}`);
  };

  return (
    <div className="p-4 overflow-x-hidden relative min-h-[75px]">
      <nav className="relative flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8 xl:gap-10 bg-gray rounded-full overflow-x-auto no-scrollbar w-full">
        {categories.map((category) => (
          <CategoryLink
            key={category.attributes.slug}
            isActive={activeCategories.includes(category.attributes.slug as string)}
            onClick={() => toggleCategoryHandler(category.attributes.slug as string)}
          >
            {category.attributes.Name}
          </CategoryLink>
        ))}
      </nav>
    </div>
  );
};

export default CategoryNav;
